/**
 * Comprehensive input validation and sanitization utilities
 * Provides XSS protection, input validation, and data sanitization
 * Enhanced with advanced security measures and robust filename generation
 */

// XSS Protection - HTML entity encoding (extended set)
const HTML_ENTITIES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;',
  '`': '&#x60;',
  '=': '&#x3D;',
  // Additional potentially dangerous characters
  '{': '&#x7B;',
  '}': '&#x7D;',
  '(': '&#x28;',
  ')': '&#x29;',
  '[': '&#x5B;',
  ']': '&#x5D;',
  ';': '&#x3B;',
  ':': '&#x3A;',
  '\\': '&#x5C;',
  '|': '&#x7C;',
  '$': '&#x24;',
  '%': '&#x25;',
  '#': '&#x23;',
  '@': '&#x40;',
  '!': '&#x21;'
};

// Common XSS attack patterns to detect and sanitize
// Note: No 'g' flag needed for .test() - it would cause lastIndex mutation and unreliable detection
const XSS_PATTERNS = [
  /javascript:/i,
  /vbscript:/i,
  /data:text\/html/i,
  /data:text\/javascript/i,
  /on\w+\s*=/i, // Event handlers like onclick, onmouseover
  /<script[^>]*>.*?<\/script>/is,
  /<iframe[^>]*>.*?<\/iframe>/is,
  /<object[^>]*>.*?<\/object>/is,
  /<embed[^>]*>/i,
  /<link[^>]*>/i,
  /<meta[^>]*>/i,
  /<style[^>]*>.*?<\/style>/is,
  /eval\s*\(/i,
  /expression\s*\(/i,
  /url\s*\(/i,
  /import\s+/i
];

// SQL injection patterns
// Note: No 'g' flag needed for .test() - it would cause lastIndex mutation and unreliable detection
const SQL_PATTERNS = [
  /(\b(ALTER|CREATE|DELETE|DROP|EXEC(UTE){0,1}|INSERT( +INTO){0,1}|MERGE|SELECT|UPDATE|UNION( +ALL){0,1})\b)/i,
  /(;|\s|^)(-{2}|\/\*|\*\/)/i,
  /(\b(CHAR|NCHAR|VARCHAR|NVARCHAR|BINARY|VARBINARY)\s*\(\s*\d+\s*\))/i
];

// Unicode normalization attacks prevention
const DANGEROUS_UNICODE = [
  /[\u200B-\u200D\uFEFF]/g, // Zero-width characters
  /[\u202A-\u202E]/g, // Text direction override
  /[\u2060-\u2064]/g, // Word joiner and invisible math operators
  /[\uFFF0-\uFFFF]/g, // Specials block
  // eslint-disable-next-line no-control-regex
  /[\u0001-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, // Control characters
  /[\u0080-\u009F]/g // More control characters
];

/**
 * Detect XSS patterns only (NOT SQL)
 * Use for: General user input where SQL keywords might be legitimate
 * @param {string} str - Input string to check
 * @returns {boolean} True if potential XSS detected
 */
const detectXSS = (str) => {
  if (typeof str !== 'string') return false;

  // ONLY check for XSS patterns
  for (const pattern of XSS_PATTERNS) {
    if (pattern.test(str)) return true;
  }

  return false;
};

/**
 * Detect SQL injection patterns only
 * Use for: Inputs that will be used in database queries (if not using ORM/parameterized queries)
 * Note: If using Supabase/Prisma/ORMs, SQL injection protection is built-in
 * @param {string} str - Input string to check
 * @returns {boolean} True if potential SQL injection detected
 */
const detectSQL = (str) => {
  if (typeof str !== 'string') return false;

  for (const pattern of SQL_PATTERNS) {
    if (pattern.test(str)) return true;
  }

  return false;
};

/**
 * Detect both XSS and SQL injection patterns
 * Use for: Critical inputs like authentication, authorization, admin operations
 * @param {string} str - Input string to check
 * @returns {boolean} True if any malicious pattern detected
 */
const detectMalicious = (str) => {
  return detectXSS(str) || detectSQL(str);
};

/**
 * Remove dangerous Unicode characters that could be used for attacks
 * @param {string} str - Input string
 * @returns {string} String with dangerous Unicode removed
 */
const removeDangerousUnicode = (str) => {
  if (typeof str !== 'string') return '';
  
  let cleaned = str;
  for (const pattern of DANGEROUS_UNICODE) {
    cleaned = cleaned.replace(pattern, '');
  }
  
  return cleaned;
};

/**
 * Advanced sanitization to prevent XSS attacks with multiple layers of protection
 * @param {string} str - Input string to sanitize
 * @param {object} options - Sanitization options
 * @returns {string} Sanitized string
 */
export const sanitizeHTML = (str, options = {}) => {
  if (typeof str !== 'string') return '';
  
  const {
    stripXSS = true,
    allowBasicMarkup = false,
    maxLength = 10000,
    preserveLineBreaks = false,
    minimalEncoding = false
  } = options;
  
  let sanitized = str;
  
  // 1. Normalize Unicode to prevent encoding attacks
  sanitized = sanitized.normalize('NFKC');
  
  // 2. Remove dangerous Unicode characters
  sanitized = removeDangerousUnicode(sanitized);
  
  // 3. Detect and handle XSS attempts
  if (stripXSS && detectXSS(sanitized)) {
    // Aggressive sanitization for detected XSS
    sanitized = sanitized.replace(/[<>'"`;(){}[\]\\|$%#@!]/g, '');
  }
  
  // 4. Handle line breaks
  if (preserveLineBreaks) {
    sanitized = sanitized.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  } else {
    sanitized = sanitized.replace(/[\r\n]/g, ' ');
  }
  
  // 5. Basic HTML entity encoding
  if (minimalEncoding) {
    // Only encode the most dangerous characters, not &
    const minimalEntityPattern = /[<>"'`]/g;
    sanitized = sanitized.replace(minimalEntityPattern, (char) => 
      HTML_ENTITIES[char] || `&#${char.charCodeAt(0)};`
    );
  } else {
    const entityPattern = allowBasicMarkup
      ? /[&<>"'`=/]/g  // Standard entities
      : /[&<>"'`=/{}[\]();\\|$%#@!]/g; // Extended for high security
      
    sanitized = sanitized.replace(entityPattern, (char) => 
      HTML_ENTITIES[char] || `&#${char.charCodeAt(0)};`
    );
  }
  
  // 6. Trim and limit length
  sanitized = sanitized.trim();
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength).trim() + '...';
  }
  
  // 7. Final safety check - remove any remaining suspicious patterns
  sanitized = sanitized.replace(/javascript:/gi, 'js-blocked:');
  sanitized = sanitized.replace(/vbscript:/gi, 'vbs-blocked:');
  sanitized = sanitized.replace(/data:text\//gi, 'data-blocked:');
  
  return sanitized;
};

/**
 * Enhanced sanitization for HTML attributes with strict security
 * @param {string} str - Input string
 * @param {object} options - Sanitization options
 * @returns {string} Sanitized attribute value
 */
export const sanitizeAttribute = (str, options = {}) => {
  if (typeof str !== 'string') return '';
  
  const { maxLength = 200, allowSpaces = true } = options;
  
  let sanitized = str;
  
  // Remove dangerous Unicode and normalize
  sanitized = removeDangerousUnicode(sanitized.normalize('NFKC'));
  
  // Strict character filtering for attributes
  const forbidden = allowSpaces 
    ? /[&<>"'`=/\n\r\t{}[\]();\\|$%#@!]/g
    : /[&<>"'`=/\n\r\t\s{}[\]();\\|$%#@!]/g;
    
  sanitized = sanitized.replace(forbidden, (char) => HTML_ENTITIES[char] || '');
  
  // Remove any protocol handlers
  sanitized = sanitized.replace(/(javascript|vbscript|data):/gi, '');
  
  // Limit length and trim
  sanitized = sanitized.trim();
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }
  
  return sanitized;
};

/**
 * Deep sanitize object - recursively sanitize all string values
 * @param {any} obj - Object to sanitize
 * @returns {any} Sanitized object
 */
export const sanitizeObject = (obj) => {
  if (typeof obj === 'string') return sanitizeHTML(obj);
  if (typeof obj !== 'object' || obj === null) return obj;

  if (Array.isArray(obj)) {
    return obj.map(sanitizeObject);
  }

  const sanitized = {};
  for (const [key, value] of Object.entries(obj)) {
    sanitized[key] = sanitizeObject(value);
  }
  return sanitized;
};

/**
 * Validate and sanitize email addresses
 * @param {string} email - Email to validate
 * @returns {{isValid: boolean, sanitized: string, error: string|null}}
 */
export const validateEmail = (email) => {
  const sanitized = (email || '').trim().toLowerCase();

  // RFC 5322 compliant email regex (simplified)
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  if (!sanitized) {
    return { isValid: false, sanitized: '', error: 'Email is required' };
  }

  if (sanitized.length > 254) {
    return { isValid: false, sanitized, error: 'Email is too long (max 254 characters)' };
  }

  if (!emailRegex.test(sanitized)) {
    return { isValid: false, sanitized, error: 'Invalid email format' };
  }

  return { isValid: true, sanitized, error: null };
};

/**
 * Validate text input with configurable constraints
 * @param {string} text - Input text
 * @param {object} options - Validation options
 * @returns {{isValid: boolean, sanitized: string, error: string|null}}
 */
export const validateText = (text, options = {}) => {
  const {
    required = false,
    minLength = 0,
    maxLength = 1000,
    pattern = null,
    fieldName = 'Field'
  } = options;

  const trimmed = (text || '').trim();
  
  // Security: Check for XSS patterns even if we don't encode HTML entities
  // This prevents storing malicious scripts that could be executed in other contexts
  if (trimmed && detectXSS(trimmed)) {
    return { isValid: false, sanitized: trimmed, error: `${fieldName} contains invalid characters or potential security risks` };
  }

  const sanitized = trimmed;

  if (required && !trimmed) {
    return { isValid: false, sanitized: '', error: `${fieldName} is required` };
  }

  if (trimmed && trimmed.length < minLength) {
    return { isValid: false, sanitized, error: `${fieldName} must be at least ${minLength} characters` };
  }

  if (trimmed && trimmed.length > maxLength) {
    return { isValid: false, sanitized, error: `${fieldName} must not exceed ${maxLength} characters` };
  }

  if (pattern && trimmed && !pattern.test(trimmed)) {
    return { isValid: false, sanitized, error: `${fieldName} format is invalid` };
  }

  return { isValid: true, sanitized, error: null };
};

/**
 * Validate numeric input
 * @param {string|number} value - Numeric value
 * @param {object} options - Validation options
 * @returns {{isValid: boolean, sanitized: number|string, error: string|null}}
 */
export const validateNumber = (value, options = {}) => {
  const {
    required = false,
    min = Number.MIN_SAFE_INTEGER,
    max = Number.MAX_SAFE_INTEGER,
    integer = false,
    fieldName = 'Field'
  } = options;

  const str = String(value || '').trim();

  if (required && !str) {
    return { isValid: false, sanitized: '', error: `${fieldName} is required` };
  }

  if (!str) {
    return { isValid: true, sanitized: '', error: null };
  }

  const num = Number(str);

  if (isNaN(num)) {
    return { isValid: false, sanitized: str, error: `${fieldName} must be a valid number` };
  }

  if (integer && !Number.isInteger(num)) {
    return { isValid: false, sanitized: str, error: `${fieldName} must be an integer` };
  }

  if (num < min) {
    return { isValid: false, sanitized: str, error: `${fieldName} must be at least ${min}` };
  }

  if (num > max) {
    return { isValid: false, sanitized: str, error: `${fieldName} must not exceed ${max}` };
  }

  return { isValid: true, sanitized: num, error: null };
};

/**
 * Validate date input
 * @param {string} dateStr - Date string
 * @param {object} options - Validation options
 * @returns {{isValid: boolean, sanitized: string, error: string|null}}
 */
export const validateDate = (dateStr, options = {}) => {
  const {
    required = false,
    minDate = null,
    maxDate = null,
    fieldName = 'Date'
  } = options;

  const sanitized = (dateStr || '').trim();

  if (required && !sanitized) {
    return { isValid: false, sanitized: '', error: `${fieldName} is required` };
  }

  if (!sanitized) {
    return { isValid: true, sanitized: '', error: null };
  }

  const date = new Date(sanitized);

  if (isNaN(date.getTime())) {
    return { isValid: false, sanitized, error: `${fieldName} is not a valid date` };
  }

  if (minDate) {
    const min = new Date(minDate);
    if (date < min) {
      return { isValid: false, sanitized, error: `${fieldName} must be after ${minDate}` };
    }
  }

  if (maxDate) {
    const max = new Date(maxDate);
    if (date > max) {
      return { isValid: false, sanitized, error: `${fieldName} must be before ${maxDate}` };
    }
  }

  return { isValid: true, sanitized, error: null };
};

/**
 * Validate dropdown/select value
 * @param {string} value - Selected value
 * @param {array} allowedValues - Array of allowed values
 * @param {object} options - Validation options
 * @returns {{isValid: boolean, sanitized: string, error: string|null}}
 */
export const validateSelect = (value, allowedValues = [], options = {}) => {
  const {
    required = false,
    fieldName = 'Field'
  } = options;

  const sanitized = sanitizeHTML((value || '').trim());

  if (required && !sanitized) {
    return { isValid: false, sanitized: '', error: `${fieldName} is required` };
  }

  if (!sanitized) {
    return { isValid: true, sanitized: '', error: null };
  }

  if (!allowedValues.includes(value)) {
    return { isValid: false, sanitized, error: `${fieldName} contains an invalid value` };
  }

  return { isValid: true, sanitized, error: null };
};

/**
 * Validate file upload
 * @param {File} file - File object
 * @param {object} options - Validation options
 * @returns {{isValid: boolean, error: string|null, metadata: object}}
 */
export const validateFileUpload = (file, options = {}) => {
  const {
    maxSize = 5 * 1024 * 1024, // 5MB default
    allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
    fieldName = 'File'
  } = options;

  if (!file) {
    return { isValid: false, error: `${fieldName} is required`, metadata: null };
  }

  // Check file size
  if (file.size > maxSize) {
    const maxMB = (maxSize / (1024 * 1024)).toFixed(1);
    return { isValid: false, error: `${fieldName} exceeds maximum size of ${maxMB}MB`, metadata: null };
  }

  // Check MIME type
  if (!allowedTypes.includes(file.type)) {
    return { isValid: false, error: `${fieldName} type not allowed. Allowed: ${allowedTypes.join(', ')}`, metadata: null };
  }

  // Check file extension
  const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
  if (!allowedExtensions.includes(extension)) {
    return { isValid: false, error: `${fieldName} extension not allowed. Allowed: ${allowedExtensions.join(', ')}`, metadata: null };
  }

  // Additional security checks
  // Check for null bytes in filename (directory traversal prevention)
  if (file.name.includes('\0')) {
    return { isValid: false, error: `${fieldName} name contains invalid characters`, metadata: null };
  }

  // Check for suspicious patterns in filename
  const suspiciousPatterns = [/\.\./g, /[<>:"|?*]/g, /^\./, /\.{2,}/];
  if (suspiciousPatterns.some(pattern => pattern.test(file.name))) {
    return { isValid: false, error: `${fieldName} name contains invalid characters`, metadata: null };
  }

  return {
    isValid: true,
    error: null,
    metadata: {
      name: file.name,
      size: file.size,
      type: file.type,
      extension,
      lastModified: file.lastModified
    }
  };
};

/**
 * Advanced filename sanitization with comprehensive edge case handling
 * Prevents directory traversal, command injection, encoding issues, and various attacks
 * @param {string} filename - Filename to sanitize
 * @param {object} options - Sanitization options
 * @returns {string} Sanitized filename
 */
export const sanitizeFileName = (filename, options = {}) => {
  const {
    maxLength = 255,
    preserveExtension = true,
    replacement = '_',
    allowSpaces = false,
    allowUnicode = false,
    strictMode = true,
    // Allow retaining '&' in filenames if desired (safe on modern filesystems)
    preserveAmpersand = false
  } = options;

  if (!filename || typeof filename !== 'string') return 'unnamed_file';

  let sanitized = filename.trim();

  // 1. Security: Remove null bytes and other dangerous characters
  // eslint-disable-next-line no-control-regex
  sanitized = sanitized.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, ''); // Control chars
  sanitized = sanitized.replace(/[\x80-\x9F]/g, ''); // Extended control chars

  // 2. Unicode normalization and dangerous character removal
  sanitized = sanitized.normalize('NFKC');
  sanitized = removeDangerousUnicode(sanitized);

  // 3. Directory traversal prevention (enhanced)
  sanitized = sanitized.replace(/\.{2,}/g, '.'); // Multiple dots
  sanitized = sanitized.replace(/^\.+/, ''); // Leading dots
  sanitized = sanitized.replace(/\.+$/, ''); // Trailing dots (except extension)

  // 4. Prevent shell injection and command execution
  // Allow '&' to pass through when preserveAmpersand is true (safe in filenames) while still blocking other metacharacters
  if (preserveAmpersand) {
    const shellChars = /[`$(){};|<>]/g; // removed &
    sanitized = sanitized.replace(shellChars, replacement);
  } else {
    const shellChars = /[`$(){};&|<>]/g;
    sanitized = sanitized.replace(shellChars, replacement);
  }

  // 5. Handle reserved Windows/Unix filenames with enhanced detection
  const reservedPatterns = [
    /^(con|prn|aux|nul|com[0-9]|lpt[0-9])$/i, // Windows reserved
    /^(con|prn|aux|nul|com[0-9]|lpt[0-9])\./i, // Windows reserved with extension
    /^\./,  // Unix hidden files starting with dot
    /^-/,   // Unix files starting with dash (can be problematic)
  ];

  const nameWithoutExt = sanitized.split('.')[0];
  if (reservedPatterns.some(pattern => pattern.test(sanitized) || pattern.test(nameWithoutExt))) {
    sanitized = `safe_${sanitized}`;
  }

  // 6. Replace filesystem-reserved characters (comprehensive)
  if (strictMode) {
    // Strict set including broader potentially problematic chars
    const strictChars = ['\\','/',':','*','?','"','<','>','|','#','%','&','{','}','+','[',']','@','!','^','~','`'];
    const filtered = preserveAmpersand ? strictChars.filter(c => c !== '&') : strictChars;
    const charClass = filtered.map(ch => ch.replace(/[-\\^]/g, '\\' + ch)).join('');
    const fsReserved = new RegExp('[' + charClass + ']', 'g');
    sanitized = sanitized.replace(fsReserved, replacement);
  } else {
    const stdChars = ['\\','/',':','*','?','"','<','>','|'];
    const charClass = stdChars.map(ch => ch.replace(/[-\\^]/g, '\\' + ch)).join('');
    const fsReserved = new RegExp('[' + charClass + ']', 'g');
    sanitized = sanitized.replace(fsReserved, replacement);
  }

  // 7. Handle spaces and whitespace
  if (!allowSpaces) {
    sanitized = sanitized.replace(/\s+/g, replacement);
  } else {
    sanitized = sanitized.replace(/\s+/g, ' ').trim(); // Normalize spaces
  }

  // 8. Unicode handling with better normalization
  if (!allowUnicode) {
    // More comprehensive Unicode normalization
    sanitized = sanitized
      .normalize('NFD') // Decompose
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .normalize('NFKC'); // Recompose

    // Replace remaining non-ASCII with transliteration or removal
    // eslint-disable-next-line no-control-regex
    sanitized = sanitized.replace(/[^\x00-\x7F]/g, (char) => {
      // Basic transliteration for common characters
      const transliterations = {
        'æ': 'ae', 'œ': 'oe', 'ß': 'ss', 'ø': 'o', 'å': 'a',
        '€': 'EUR', '£': 'GBP', '¥': 'YEN', '©': 'c', '®': 'r',
        '™': 'tm', '±': 'plus-minus', '°': 'deg', 'µ': 'micro'
      };
      return transliterations[char] || replacement;
    });
  }

  // 9. Cleanup multiple replacement characters
  const replacementRegex = new RegExp(`${escapeRegExp(replacement)}{2,}`, 'g');
  sanitized = sanitized.replace(replacementRegex, replacement);

  // 10. Handle extension with validation
  let name = sanitized;
  let ext = '';

  if (preserveExtension && sanitized.includes('.')) {
    const lastDotIndex = sanitized.lastIndexOf('.');
    if (lastDotIndex > 0 && lastDotIndex < sanitized.length - 1) {
      name = sanitized.substring(0, lastDotIndex);
      ext = sanitized.substring(lastDotIndex);

      // Validate and sanitize extension
      ext = ext.toLowerCase();
      ext = ext.replace(/[^a-z0-9.]/g, '');
      
      // Ensure extension doesn't exceed reasonable length
      if (ext.length > 10) {
        ext = ext.substring(0, 10);
      }
      
      // Validate against known safe extensions (optional strict check)
      if (strictMode) {
        const safeExtensions = [
          '.txt', '.md', '.json', '.csv', '.xml', '.log',
          '.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg',
          '.mp4', '.mp3', '.wav', '.pdf', '.doc', '.docx',
          '.xls', '.xlsx', '.ppt', '.pptx', '.zip', '.tar',
          '.gz', '.js', '.css', '.html', '.htm'
        ];
        
        if (!safeExtensions.includes(ext)) {
          console.warn(`Potentially unsafe file extension: ${ext}`);
          // Don't block, just warn
        }
      }
    }
  }

  // 11. Remove replacement characters from start and end
  const trimRegex = new RegExp(`^${escapeRegExp(replacement)}+|${escapeRegExp(replacement)}+$`, 'g');
  name = name.replace(trimRegex, '');

  // 12. Enforce maximum length (accounting for extension)
  const maxNameLength = Math.max(1, maxLength - ext.length);
  if (name.length > maxNameLength) {
    name = name.substring(0, maxNameLength).replace(trimRegex, '');
  }

  // 13. Final assembly and validation
  sanitized = name + ext;

  // 14. Final safety checks
  if (!sanitized || sanitized === ext || sanitized.length === 0) {
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2, 8);
    sanitized = `file_${timestamp}_${randomId}${ext}`;
  }

  // 15. Ensure minimum length
  if (sanitized.length < 1) {
    sanitized = `file_${Date.now()}`;
  }

  // 16. Final length check
  if (sanitized.length > maxLength) {
    const overflow = sanitized.length - maxLength;
    const nameWithoutExt = sanitized.substring(0, sanitized.lastIndexOf('.') || sanitized.length);
    const extension = sanitized.substring(sanitized.lastIndexOf('.') || sanitized.length);
    
    const truncatedName = nameWithoutExt.substring(0, nameWithoutExt.length - overflow);
    sanitized = truncatedName + extension;
  }

  return sanitized;
};

/**
 * Helper function to escape special regex characters
 * @param {string} string - String to escape
 * @returns {string} Escaped string
 */
const escapeRegExp = (string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * Generate safe filename from row data with enhanced sanitization and intelligent truncation
 * @param {object} row - Row data object
 * @param {object} options - Generation options
 * @returns {string} Safe filename
 */
export const generateSafeFileName = (row, options = {}) => {
  const {
    maxLength = 200, // Shorter for better compatibility
    separator = '___',
    includeTimestamp = false,
    priorityFields = ['code', 'client', 'page'], // Fields that should never be truncated
    strictMode = true,
    preserveAmpersand = false
  } = options;

  if (!row || typeof row !== 'object') {
    return generateFallbackFilename();
  }

  const parts = [];
  const priorities = [];

  // Helper to add sanitized parts with priority tracking
  const addPart = (value, fieldName = '', isPriority = false) => {
    if (!value || value === '') return;

    // For filenames, skip HTML entity encoding entirely
    // Just use the value directly - sanitizeFileName will handle filesystem safety
    const cleanValue = String(value);

    const sanitized = sanitizeFileName(cleanValue, {
      maxLength: isPriority ? 50 : 30, // Priority fields get more space
      preserveExtension: false,
      replacement: '-',
      allowSpaces: false,
      allowUnicode: false,
      strictMode,
      preserveAmpersand
    });

    if (sanitized && sanitized !== 'unnamed_file') {
      const partIndex = parts.length;
      parts.push(sanitized);
      
      if (isPriority) {
        priorities.push(partIndex);
      }
      
      // Add separator (will be cleaned up later)
      if (parts.length > 0) {
        parts.push(separator);
      }
    }
  };

  // 1. Add code (highest priority - always included)
  const code = row.code || generateRandomCode();
  addPart(code, 'code', true);

  // 2. Add client (high priority)
  addPart(row.client, 'client', priorityFields.includes('client'));

  // 3. Add page (high priority)
  addPart(row.page, 'page', priorityFields.includes('page'));

  // 4. Add section (medium priority)
  addPart(row.section, 'section', priorityFields.includes('section'));

  // 5. Add device information (medium priority)
  addPart(row.device, 'device', priorityFields.includes('device'));

  // 6. Add usage (medium priority)
  addPart(row.usage, 'usage', priorityFields.includes('usage'));

  // 7. Add description (lower priority)
  addPart(row.description, 'description', priorityFields.includes('description'));

  // 8. Add person names (lower priority)
  if (row.first || row.last) {
    const fullName = [row.first, row.last].filter(Boolean).join('-');
    addPart(fullName, 'names', priorityFields.includes('names'));
  }

  // 9. Handle dimensions specially
  if (row.width || row.height) {
    const width = String(row.width || ''); // No HTML encoding needed
    const height = String(row.height || ''); // No HTML encoding needed
    
    // Validate dimensions are numeric
    const validWidth = /^\d+$/.test(width) ? width : '';
    const validHeight = /^\d+$/.test(height) ? height : '';
    
    if (validWidth || validHeight) {
      const dimensions = `${validWidth || '0'}x${validHeight || '0'}`;
      addPart(dimensions, 'dimensions', priorityFields.includes('dimensions'));
    }
  }

  // 10. Add employee (medium priority)
  addPart(row.employee, 'employee', priorityFields.includes('employee'));

  // 11. Handle date with validation
  if (row.date) {
    const date = new Date(row.date);
    if (!isNaN(date.getTime()) && date.getFullYear() > 1900 && date.getFullYear() < 2100) {
      const dateStr = date.toISOString().split('T')[0];
      addPart(dateStr, 'date', priorityFields.includes('date'));
    }
  }

  // 12. Add timestamp if requested
  if (includeTimestamp) {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
    addPart(timestamp, 'timestamp', false);
  }

  // Clean up trailing separator
  if (parts.length > 0 && parts[parts.length - 1] === separator) {
    parts.pop();
  }

  // 13. Intelligent truncation if too long
  let filename = parts.join('');
  
  if (filename.length > maxLength) {
    filename = intelligentTruncation(parts, separator, maxLength, priorities);
  }

  // 14. Final sanitization pass
  filename = sanitizeFileName(filename, {
    maxLength,
    preserveExtension: false,
    replacement: '-',
    allowSpaces: false,
    strictMode,
    preserveAmpersand
  });

  // 15. Fallback if sanitization resulted in empty or too short filename
  if (!filename || filename.length < 3 || filename === 'unnamed_file') {
    filename = generateFallbackFilename(code);
  }

  // 16. Final validation
  if (!isValidFilename(filename)) {
    console.warn('Generated filename failed validation, using fallback');
    filename = generateFallbackFilename(code);
  }

  return filename;
};

/**
 * Intelligent truncation that preserves important parts
 * @param {Array} parts - Array of filename parts
 * @param {string} separator - Separator string
 * @param {number} maxLength - Maximum length
 * @param {Array} priorities - Array of priority part indices
 * @returns {string} Truncated filename
 */
const intelligentTruncation = (parts, separator, maxLength, priorities) => {
  let result = parts.join('');
  
  if (result.length <= maxLength) return result;

  // Remove non-priority parts first, starting from the end
  const partsCopy = [...parts];
  const prioritySet = new Set(priorities);
  
  for (let i = partsCopy.length - 1; i >= 0 && result.length > maxLength; i--) {
    if (!prioritySet.has(Math.floor(i / 2)) && partsCopy[i] !== separator) {
      // Remove this part and its separator
      partsCopy.splice(i, 1);
      if (i > 0 && partsCopy[i - 1] === separator) {
        partsCopy.splice(i - 1, 1);
        i--; // Adjust index after removal
      }
      result = partsCopy.join('');
    }
  }

  // If still too long, truncate priority parts proportionally
  if (result.length > maxLength) {
    const excess = result.length - maxLength;
    const nonSeparatorParts = partsCopy.filter(part => part !== separator);
    const avgReduction = Math.ceil(excess / nonSeparatorParts.length);
    
    for (let i = 0; i < partsCopy.length; i++) {
      if (partsCopy[i] !== separator && partsCopy[i].length > avgReduction) {
        partsCopy[i] = partsCopy[i].substring(0, partsCopy[i].length - avgReduction);
      }
    }
    
    result = partsCopy.join('');
  }

  // Final truncation if still too long
  if (result.length > maxLength) {
    result = result.substring(0, maxLength);
    // Ensure we don't end with a separator
    while (result.endsWith(separator)) {
      result = result.substring(0, result.length - separator.length);
    }
  }

  return result;
};

/**
 * Generate a fallback filename when normal generation fails
 * @param {string} code - Optional code to include
 * @returns {string} Fallback filename
 */
const generateFallbackFilename = (code = null) => {
  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substring(2, 8);
  const safeCode = code ? sanitizeFileName(code, { maxLength: 10, strictMode: true }) : 'file';
  return `${safeCode}_${timestamp}_${randomId}`;
};

/**
 * Generate a random alphanumeric code
 * @param {number} length - Length of the code
 * @returns {string} Random code
 */
const generateRandomCode = (length = 5) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * Validate if a filename is safe and properly formatted
 * @param {string} filename - Filename to validate
 * @returns {boolean} True if valid
 */
const isValidFilename = (filename) => {
  if (!filename || typeof filename !== 'string') return false;
  if (filename.length === 0 || filename.length > 255) return false;
  if (/^\.+$/.test(filename)) return false; // Only dots
  if (/[\\/:*?"<>|]/.test(filename)) return false; // Invalid chars
  if (/^(con|prn|aux|nul|com[0-9]|lpt[0-9])$/i.test(filename.split('.')[0])) return false;
  return true;
};

/**
 * Advanced URL validation and sanitization
 * @param {string} url - URL to validate
 * @param {object} options - Validation options
 * @returns {{isValid: boolean, sanitized: string, error: string|null}}
 */
export const validateURL = (url, options = {}) => {
  const {
    required = false,
    allowedProtocols = ['http:', 'https:'],
    allowedDomains = null, // null means any domain
    maxLength = 2048,
    fieldName = 'URL'
  } = options;

  const sanitized = sanitizeHTML((url || '').trim(), { stripXSS: true });

  if (required && !sanitized) {
    return { isValid: false, sanitized: '', error: `${fieldName} is required` };
  }

  if (!sanitized) {
    return { isValid: true, sanitized: '', error: null };
  }

  if (sanitized.length > maxLength) {
    return { isValid: false, sanitized, error: `${fieldName} exceeds maximum length of ${maxLength} characters` };
  }

  try {
    const urlObj = new URL(sanitized);

    // Check protocol
    if (!allowedProtocols.includes(urlObj.protocol)) {
      return { isValid: false, sanitized, error: `${fieldName} protocol not allowed. Allowed: ${allowedProtocols.join(', ')}` };
    }

    // Check domain if specified
    if (allowedDomains && !allowedDomains.includes(urlObj.hostname)) {
      return { isValid: false, sanitized, error: `${fieldName} domain not allowed` };
    }

    // Additional security checks
    // eslint-disable-next-line no-script-url
    if (urlObj.protocol === 'javascript:' || urlObj.protocol === 'data:') {
      return { isValid: false, sanitized, error: `${fieldName} contains potentially dangerous protocol` };
    }

    return { isValid: true, sanitized: urlObj.toString(), error: null };
  } catch (error) {
    return { isValid: false, sanitized, error: `${fieldName} is not a valid URL` };
  }
};

/**
 * Validate and sanitize phone numbers
 * @param {string} phone - Phone number to validate
 * @param {object} options - Validation options
 * @returns {{isValid: boolean, sanitized: string, error: string|null}}
 */
export const validatePhone = (phone, options = {}) => {
  const {
    required = false,
    format = 'international', // 'us', 'international', 'any'
    fieldName = 'Phone number'
  } = options;

  let sanitized = sanitizeHTML((phone || '').trim(), { stripXSS: true });

  if (required && !sanitized) {
    return { isValid: false, sanitized: '', error: `${fieldName} is required` };
  }

  if (!sanitized) {
    return { isValid: true, sanitized: '', error: null };
  }

  // Remove common formatting characters
  const digitsOnly = sanitized.replace(/[\s\-()+ .[\]]/g, '');

  let isValid = false;
  let formattedPhone = sanitized;

  switch (format) {
    case 'us':
      // US phone number: 10 digits, optionally starting with 1
      if (/^1?[2-9]\d{2}[2-9]\d{2}\d{4}$/.test(digitsOnly)) {
        isValid = true;
        const cleaned = digitsOnly.replace(/^1/, ''); // Remove leading 1
        formattedPhone = `+1-${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
      }
      break;
    case 'international':
      // International format: 7-15 digits with optional country code
      if (/^\+?[1-9]\d{6,14}$/.test(digitsOnly)) {
        isValid = true;
        formattedPhone = digitsOnly.startsWith('+') ? sanitized : `+${digitsOnly}`;
      }
      break;
    case 'any':
      // Any format: at least 7 digits
      if (/\d{7,}/.test(digitsOnly) && digitsOnly.length >= 7 && digitsOnly.length <= 15) {
        isValid = true;
        formattedPhone = sanitized;
      }
      break;
    default:
      // Default to 'any' format validation
      if (/\d{7,}/.test(digitsOnly) && digitsOnly.length >= 7 && digitsOnly.length <= 15) {
        isValid = true;
        formattedPhone = sanitized;
      }
      break;
  }

  if (!isValid) {
    return { isValid: false, sanitized, error: `${fieldName} format is invalid` };
  }

  return { isValid: true, sanitized: formattedPhone, error: null };
};

/**
 * Validate and sanitize JSON input
 * @param {string} jsonString - JSON string to validate
 * @param {object} options - Validation options
 * @returns {{isValid: boolean, sanitized: object|string, error: string|null}}
 */
export const validateJSON = (jsonString, options = {}) => {
  const {
    required = false,
    maxDepth = 10,
    maxKeys = 100,
    fieldName = 'JSON'
  } = options;

  const sanitized = sanitizeHTML((jsonString || '').trim(), { stripXSS: true });

  if (required && !sanitized) {
    return { isValid: false, sanitized: '', error: `${fieldName} is required` };
  }

  if (!sanitized) {
    return { isValid: true, sanitized: '', error: null };
  }

  try {
    const parsed = JSON.parse(sanitized);

    // Check depth and key count
    const validation = validateJSONStructure(parsed, maxDepth, maxKeys);
    if (!validation.isValid) {
      return { isValid: false, sanitized, error: `${fieldName} ${validation.error}` };
    }

    return { isValid: true, sanitized: parsed, error: null };
  } catch (error) {
    return { isValid: false, sanitized, error: `${fieldName} is not valid JSON: ${error.message}` };
  }
};

/**
 * Validate JSON structure for depth and complexity
 * @param {any} obj - Parsed JSON object
 * @param {number} maxDepth - Maximum nesting depth
 * @param {number} maxKeys - Maximum number of keys
 * @param {number} currentDepth - Current depth (internal)
 * @returns {{isValid: boolean, error: string|null}}
 */
const validateJSONStructure = (obj, maxDepth, maxKeys, currentDepth = 0) => {
  if (currentDepth > maxDepth) {
    return { isValid: false, error: `exceeds maximum depth of ${maxDepth}` };
  }

  if (typeof obj === 'object' && obj !== null) {
    const keys = Object.keys(obj);
    if (keys.length > maxKeys) {
      return { isValid: false, error: `exceeds maximum keys of ${maxKeys}` };
    }

    for (const key of keys) {
      const result = validateJSONStructure(obj[key], maxDepth, maxKeys, currentDepth + 1);
      if (!result.isValid) return result;
    }
  }

  return { isValid: true, error: null };
};

/**
 * Enhanced batch validation with detailed reporting
 * @param {object} fields - Object with field values
 * @param {object} validators - Object with validator functions
 * @param {object} options - Validation options
 * @returns {{isValid: boolean, errors: object, sanitized: object, summary: object}}
 */
export const validateFields = (fields, validators, options = {}) => {
  const {
    stopOnFirstError = false,
    sanitizeAll = true,
    logWarnings = true
  } = options;

  const errors = {};
  const sanitized = {};
  const warnings = [];
  let isValid = true;
  let fieldsProcessed = 0;
  let errorsFound = 0;

  for (const [fieldName, value] of Object.entries(fields)) {
    fieldsProcessed++;
    const validator = validators[fieldName];

    if (validator) {
      try {
        const result = validator(value);

        if (!result.isValid) {
          isValid = false;
          errorsFound++;
          errors[fieldName] = result.error;

          if (stopOnFirstError) {
            break;
          }
        }

        sanitized[fieldName] = result.sanitized;
      } catch (error) {
        isValid = false;
        errorsFound++;
        errors[fieldName] = `Validation error: ${error.message}`;
        
        if (logWarnings) {
          warnings.push(`Validator for ${fieldName} threw an error: ${error.message}`);
        }

        // Default sanitization on error
        if (sanitizeAll) {
          sanitized[fieldName] = sanitizeHTML(String(value || ''));
        }

        if (stopOnFirstError) {
          break;
        }
      }
    } else {
      // Default sanitization if no validator
      if (sanitizeAll) {
        sanitized[fieldName] = sanitizeHTML(String(value || ''));
      } else {
        sanitized[fieldName] = value;
      }

      if (logWarnings && value) {
        warnings.push(`No validator found for field: ${fieldName}`);
      }
    }
  }

  const summary = {
    fieldsProcessed,
    errorsFound,
    warningsCount: warnings.length,
    validationRate: ((fieldsProcessed - errorsFound) / fieldsProcessed * 100).toFixed(1)
  };

  // Validation warnings available in return value if needed

  return { isValid, errors, sanitized, summary, warnings };
};

/**
 * Create a validation schema for consistent field validation
 * @param {object} schema - Schema definition
 * @returns {function} Validator function
 */
export const createValidationSchema = (schema) => {
  return (data) => {
    const validators = {};

    for (const [fieldName, rules] of Object.entries(schema)) {
      validators[fieldName] = (value) => {
        // Apply rules in order
        for (const rule of rules) {
          const result = rule(value);
          if (!result.isValid) {
            return result;
          }
          value = result.sanitized; // Use sanitized value for next rule
        }
        return { isValid: true, sanitized: value, error: null };
      };
    }

    return validateFields(data, validators, { logWarnings: true });
  };
};

// Export utility functions as named exports for use in other modules
export { detectXSS, detectSQL, detectMalicious, removeDangerousUnicode, isValidFilename, generateRandomCode };

const validation = {
  // Core sanitization
  sanitizeHTML,
  sanitizeAttribute,
  sanitizeObject,

  // Field validation
  validateEmail,
  validateText,
  validateNumber,
  validateDate,
  validateSelect,
  validateFileUpload,
  validateURL,
  validatePhone,
  validateJSON,

  // Filename handling
  sanitizeFileName,
  generateSafeFileName,

  // Batch validation
  validateFields,
  createValidationSchema,

  // Utility functions
  detectXSS,
  detectSQL,
  detectMalicious,
  removeDangerousUnicode,
  isValidFilename,
  generateRandomCode
};

export default validation;
